import Vue from 'vue';
import AuthService from '@/services/AuthService.js';
import isEmpty from 'lodash/isEmpty';
import upperFirst from 'lodash/upperFirst';
import axios from 'axios';
import EventBus from '@/helpers/EventBus.js';
import { storeCurrentUser, loadCurrentUser } from '@/utils/storage';

export const namespaced = true;

const roleValid = (role, currentRole) => {
  let roleMatched = role.split('_').filter((e) => e == currentRole);

  return roleMatched.length > 0;
};

export const state = {
  localSessionRestored: false,
  currentUser: {},
};

export const mutations = {
  SET_CURRENT_USER(state, userData) {
    state.currentUser = userData;
    if (userData.role == 'tie') {
      //TIEユーザは他の権限のユーザーと違い、nameではなく、usernameにユーザーの名前が入っているのでここでcurrentUser.nameに入れる
      state.currentUser.name = userData.username;
    }
    storeCurrentUser(userData);

    // Setup axios hook to add api token before each request sending
    axios.interceptors.request.use((config) => {
      config.headers = { 'Api-Token': state.currentUser.token };
      return config;
    });
  },
  CLEAR_CURRENT_USER(state) {
    state.currentUser = {};
    localStorage.removeItem('currentUser');
  },
};

export const actions = {
  login({ commit, dispatch }, { role, username, password }) {
    Vue.$logger.debug('Login Type selected:', role);

    return new Promise((resolve, reject) => {
      AuthService.login(role, username, password)
        .then((resp) => {
          if (!roleValid(role, resp.data.role)) {
            throw { response: { data: { error: 'ログインタイプが無効です' } } };
          }

          // Update user data to vuex state
          commit('SET_CURRENT_USER', resp.data);

          // Set current role for homepage display
          let currentRole = upperFirst(resp.data.role);
          let currentProfileId = resp.data.profileId;
          Vue.$logger.debug('CurrentRole:', currentRole);
          dispatch('role/fetchCurrentRole', currentRole, { root: true });
          dispatch('role/fetchUserProfile', { currentRole, currentProfileId }, { root: true });

          // the same with return data to next then handler
          resolve(resp);
        })
        .catch((error) => {
          Vue.$logger.error('[Auth#login]', error);

          // the same with return error to next error handler
          reject(error);
        });
    });
  },
  googleAuth({ commit, dispatch }, { token }) {
    return new Promise((resolve, reject) => {
      AuthService.googleAuth(token)
        .then((resp) => {
          // Update user data to vuex state
          commit('SET_CURRENT_USER', resp.data);

          // Set current role for homepage display
          let currentRole = upperFirst(resp.data.role);
          let currentProfileId = resp.data.profileId;
          Vue.$logger.debug('CurrentRole:', currentRole);
          dispatch('role/fetchCurrentRole', currentRole, { root: true });
          dispatch('role/fetchUserProfile', { currentRole, currentProfileId }, { root: true });

          // the same with return data to next then handler
          resolve(resp);
        })
        .catch((error) => {
          Vue.$logger.error('[Auth#login]', error);

          // the same with return error to next error handler
          reject(error);
        });
    });
  },
  restoreSession({ commit, dispatch, state }) {
    if (state.localSessionRestored) {
      return;
    }

    let userData = loadCurrentUser();
    if (userData && userData.token) {
      Vue.$logger.debug('[APP]', 'Previous session found! Restoring ...');

      state.localSessionRestored = true;
      // Restore user data and token from local storage to state
      commit('SET_CURRENT_USER', userData);

      // Set current role for homepage display from local storage to state
      let currentRole = upperFirst(userData.role);
      dispatch('role/fetchCurrentRole', currentRole, { root: true });
    } else {
      Vue.$logger.error('[Auth#auth] User token blank!');
      commit('CLEAR_CURRENT_USER');
    }
  },
  verifySession({ commit, dispatch }) {
    // Double check user token still valid
    AuthService.auth()
      .then((resp) => {
        // Update latest user data to vuex state
        commit('SET_CURRENT_USER', resp.data);

        // Set current role for homepage display
        let currentRole = upperFirst(resp.data.role);
        let currentProfileId = resp.data.profileId;
        Vue.$logger.debug('[APP] CurrentRole restored:', currentRole);
        dispatch('role/fetchCurrentRole', currentRole, { root: true });
        dispatch('role/fetchUserProfile', { currentRole, currentProfileId }, { root: true });

        const hasAdminPrivilege = currentRole === 'Tie';
        EventBus.$emit('SESSION_RESTORED', { currentRole, hasAdminPrivilege });
      })
      .catch((error) => {
        Vue.$logger.error('[Auth#auth]', error.message);
        commit('CLEAR_CURRENT_USER');
      });
  },
  logout({ commit }) {
    commit('CLEAR_CURRENT_USER');
  },
};

export const getters = {
  loggedIn(state) {
    return !isEmpty(state.currentUser);
  },
  loggedInAsCorporation(state) {
    return state.currentUser.role == 'corporation';
  },
  loggedInAsBrand(state) {
    return state.currentUser.role == 'brand';
  },
  loggedInAsSchool(state) {
    return state.currentUser.role == 'school';
  },
  loggedInAsTeacher(state) {
    return state.currentUser.role == 'teacher';
  },
};
